import React, { useEffect, useState, useCallback } from 'react';
import { useUIStore } from '../../stores/uiStore';
import { Can } from '../../lib/rbac';

const SUBGROUP_DESCRIPTIONS: Record<string, string> = {
  PQT: 'Re-uploads pictures from Amazon high-res',
  DOMAIN: 'Deletes and re-enqueues for correct category',
  OPT_OBEY: 'Associates catalog product via GTIN',
  DUPLIS: 'Deletes duplicate site item',
  LINKS: 'Removes contact info from title/description',
  BRAND_PROTECTION: 'Flags for manual brand accreditation',
  DESC: 'Re-confirms price to clear flag',
  ATTRS: 'AI fills missing attributes from Amazon data',
  UNKNOWN: 'AI diagnosis for unclassified issues',
};

const ACTION_OPTIONS = [
  { value: 'auto_fix', label: 'Auto Fix' },
  { value: 'pending_review', label: 'Pending Review' },
  { value: 'skip', label: 'Skip' },
];

interface SubgroupRule {
  enabled: boolean;
  action: string;
  label?: string;
}

interface ConfigData {
  subgroup_rules: Record<string, SubgroupRule>;
}

export default function ListingFixerConfig() {
  const { sellerId } = useUIStore();
  const [config, setConfig] = useState<ConfigData | null>(null);
  const [original, setOriginal] = useState<ConfigData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const loadConfig = useCallback(async () => {
    if (!sellerId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/ai/listing-fixer/config?seller_id=${sellerId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
      });
      const json = await res.json();
      if (json.success && json.data) {
        setConfig(json.data);
        setOriginal(JSON.parse(JSON.stringify(json.data)));
      } else {
        setError(json.error || 'Error loading config');
      }
    } catch {
      setError('Error loading config');
    } finally {
      setLoading(false);
    }
  }, [sellerId]);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const isDirty = config && original
    ? JSON.stringify(config) !== JSON.stringify(original)
    : false;

  const updateRule = (key: string, field: 'enabled' | 'action', value: boolean | string) => {
    if (!config) return;
    setConfig(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        subgroup_rules: {
          ...prev.subgroup_rules,
          [key]: { ...prev.subgroup_rules[key], [field]: value },
        },
      };
    });
    setSaved(false);
  };

  const handleSave = async () => {
    if (!config || !sellerId) return;
    setSaving(true);
    setError(null);
    try {
      // Strip label before saving
      const rulesToSave: Record<string, { enabled: boolean; action: string }> = {};
      for (const [key, rule] of Object.entries(config.subgroup_rules)) {
        rulesToSave[key] = { enabled: rule.enabled, action: rule.action };
      }
      const res = await fetch(`/api/ai/listing-fixer/config?seller_id=${sellerId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({ subgroup_rules: rulesToSave }),
      });
      const json = await res.json();
      if (json.success) {
        setOriginal(JSON.parse(JSON.stringify(config)));
        setSaved(true);
        setTimeout(() => setSaved(false), 2500);
      } else {
        setError(json.error || 'Error saving config');
      }
    } catch {
      setError('Error saving config');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="py-6 text-center text-xs text-[var(--color-text-tertiary)]">
        Loading config...
      </div>
    );
  }

  if (error && !config) {
    return (
      <div className="py-4 text-center text-xs text-red-500">{error}</div>
    );
  }

  if (!config) return null;

  const rules = config.subgroup_rules;

  return (
    <div className="mt-4 bg-white rounded-lg border border-[var(--color-border)] overflow-hidden">
      <div className="px-4 py-2.5 bg-[var(--color-bg-secondary)] border-b border-[var(--color-border)] flex items-center justify-between">
        <span className="text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wide">
          Moderation Rules
        </span>
        <div className="flex items-center gap-2">
          {error && (
            <span className="text-xs text-red-500">{error}</span>
          )}
          {saved && (
            <span className="text-xs text-green-600 font-medium">Saved</span>
          )}
          <Can cap="ai.toggle_run">
          <button
            onClick={handleSave}
            disabled={!isDirty || saving}
            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
              isDirty && !saving
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-[var(--color-bg-tertiary)] text-[var(--color-text-tertiary)] cursor-not-allowed'
            }`}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          </Can>
        </div>
      </div>

      <table className="w-full text-xs">
        <thead>
          <tr className="bg-[var(--color-bg-secondary)] border-b border-[var(--color-border)]">
            <th className="text-left px-4 py-2 font-medium text-[var(--color-text-secondary)] w-1/3">
              Moderation Type
            </th>
            <th className="text-left px-4 py-2 font-medium text-[var(--color-text-secondary)] w-1/3">
              Description
            </th>
            <th className="text-center px-4 py-2 font-medium text-[var(--color-text-secondary)] w-16">
              Enabled
            </th>
            <th className="text-left px-4 py-2 font-medium text-[var(--color-text-secondary)]">
              Action
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--color-border)]">
          {Object.entries(rules).map(([key, rule]) => {
            const label = rule.label || key;
            const description = SUBGROUP_DESCRIPTIONS[key] || '';
            const isEnabled = rule.enabled;
            return (
              <tr
                key={key}
                className={`transition-opacity ${!isEnabled ? 'opacity-50' : ''}`}
              >
                <td className="px-4 py-2.5">
                  <span className="font-medium text-[var(--color-text)]">{label}</span>
                  <span className="ml-1.5 text-[var(--color-text-tertiary)] font-mono">
                    {key !== label ? `(${key})` : ''}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-[var(--color-text-secondary)]">
                  {description}
                </td>
                <td className="px-4 py-2.5 text-center">
                  <input
                    type="checkbox"
                    checked={isEnabled}
                    onChange={e => updateRule(key, 'enabled', e.target.checked)}
                    className="w-4 h-4 rounded border-[var(--color-border)] accent-blue-600 cursor-pointer"
                  />
                </td>
                <td className="px-4 py-2.5">
                  <select
                    value={rule.action}
                    onChange={e => updateRule(key, 'action', e.target.value)}
                    disabled={!isEnabled}
                    className="px-2 py-1 rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {ACTION_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
